import cn from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import openPopupOperation from 'editor/src/store/app/module/popup/operation/openPopupOperation';
import applyStyleToAllConditionTextsOperation from 'editor/src/store/design/operation/applyStyleToAllConditionTextsOperation';
import deleteConditionOperation from 'editor/src/store/design/operation/deleteConditionOperation';
import duplicateConditionOperation from 'editor/src/store/design/operation/duplicateConditionOperation';
import importConditionOperation from 'editor/src/store/design/operation/importConditionOperation';
import updateConditionActiveOptionOperation from 'editor/src/store/design/operation/updateConditionActiveOptionOperation';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import getConditionImportImpact from 'editor/src/store/design/selector/getConditionImportImpact';
import getSpreadPageMediaElements from 'editor/src/store/design/selector/getSpreadPageMediaElements';
import { ConditionGroup, Condition } from 'editor/src/store/design/types';
import { validateImportedConditionFile } from 'editor/src/store/design/util/conditionTransfer';
import removeAllSelectedMediaElementsOperation from 'editor/src/store/editor/operation/removeAllSelectedMediaElementsOperation';
import { useDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { logEvent } from 'editor/src/amplitude';
import toastController from 'editor/src/util/toastController';

import ConditionElements from 'editor/src/component/ConditionGroupBuilder/ConditionElements';
import useDragHandle from 'editor/src/component/ConditionGroupBuilder/ConditionElements/ReorganizableList/useDragHandle';
import useOverlay from 'editor/src/component/ConditionGroupBuilder/Overlay/useOverlay';
import IconApplyToAllAreas from 'editor/src/component/Icon/IconApplyToAllAreas';
import IconBin from 'editor/src/component/Icon/IconBin';
import IconCopy from 'editor/src/component/Icon/IconCopy';
import IconDownload from 'editor/src/component/Icon/IconDownload';
import IconEdit from 'editor/src/component/Icon/IconEdit';
import IconImport from 'editor/src/component/Icon/IconImport';
import PersonalizationElements from 'editor/src/component/PersonalizationContent/PersonalizationElements/index';
import SettingsMenu from 'editor/src/component/PersonalizationContent/PersonalizationElements/SettingsMenu';
import SettingsMenuItem from 'editor/src/component/PersonalizationContent/PersonalizationElements/SettingsMenu/SettingsMenuItem';
import useFocusElementOnFlag from 'editor/src/component/PersonalizationContent/PersonalizationElements/useFocusElementOnFlag';
import { PopupName } from 'editor/src/component/Popup/popups';

import ConditionOptions from './ConditionOptions';
import ConditionSettings from './ConditionSettings';
import downloadCondition from './downloadCondition';
import { consumePendingOpen, requestOpenOnMount } from './pendingConditionToOpen';

import styles from './index.module.scss';

interface Props {
  spreadIndex: number;
  conditionGroup: ConditionGroup;
  condition: Condition;
  isBuilding: boolean;
  textOnly: boolean;
  /** This card is BORROWED: the condition is design-scoped
   *  (`showOnAllSpreads`) but stored on another spread, and this spread's panel
   *  renders it so neither buyer nor author has to go looking for it.
   *
   *  Drops the ELEMENTS linked to the active option — they live on the owning
   *  spread and belong in its panel, not repeated on every page. Nested
   *  picklists are kept in the buyer view: they are controls, not content, and
   *  the reason the parent is shown everywhere applies to them just as much.
   *
   *  Combined with `isBuilding` it also marks the card as foreign: still fully
   *  editable through the menu below, but it cannot be reordered, does not
   *  offer the actions that reach into elements it is not showing, and does not
   *  duplicate the owning panel's authoring tree. */
  optionsOnly?: boolean;
  onConditionCreated?: (conditionId: string, edit: () => void) => void;
}

function PersonalizationCondition({
  condition,
  spreadIndex,
  conditionGroup,
  isBuilding,
  onConditionCreated,
  textOnly,
  optionsOnly,
}: Props) {
  const children = conditionGroup.children[getConditionChildrenKey(condition.id, condition.activeOptionId)] || [];
  const dispatch = useDispatch();
  const dragHandle = useDragHandle();
  const { t } = useTranslation();

  const conditionEditorOverlay = useOverlay();
  const divRef = useRef<HTMLDivElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Read from the store at click time rather than subscribed: both are only
  // needed when Export / Import is used, and a subscription would re-render
  // every condition card on every design change (the impact is a new object
  // each time, and the media array changes on every move).
  const readStore = <T,>(read: (state: RootState) => T): T => dispatch((_, getState) => read(getState()));

  function onSelectOption(conditionId: string, optionId: string) {
    dispatch(updateConditionActiveOptionOperation({ spreadIndex, conditionId }, optionId));
  }

  function onEdit() {
    dispatch(removeAllSelectedMediaElementsOperation());
    const rect = divRef.current?.getBoundingClientRect();
    conditionEditorOverlay.open(rect);
  }

  function onDelete() {
    dispatch(deleteConditionOperation({ spreadIndex, conditionId: condition.id }));
    logEvent('condtion deleted', undefined);
  }

  function onApplyStyleToAll() {
    dispatch(
      applyStyleToAllConditionTextsOperation(spreadIndex, { spreadIndex, conditionId: condition.id }, condition),
    );
  }

  function onDuplicate() {
    const newConditionId = dispatch(duplicateConditionOperation({ spreadIndex, conditionId: condition.id }));
    if (newConditionId) {
      // The duplicate's own card hasn't mounted yet — it opens its settings
      // itself once it does, see the useEffect below.
      requestOpenOnMount(newConditionId);
    }
    logEvent('condtion duplicated', undefined);
  }

  function onExport() {
    downloadCondition(
      condition,
      conditionGroup,
      readStore((state) => getSpreadPageMediaElements(state, spreadIndex, 0)) ?? [],
    );
    logEvent('condtion exported', undefined);
  }

  function onImportClick() {
    importInputRef.current?.click();
  }

  async function onImportFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Always reset, so re-picking the same file still fires a change event.
    event.target.value = '';
    if (!file) {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      parsed = undefined;
    }

    const result = parsed !== undefined ? validateImportedConditionFile(parsed) : { valid: false as const };
    if (!result.valid) {
      toastController.error(
        t('editor-condition-import-invalid-title', 'Import failed'),
        t('editor-condition-import-invalid', 'This file is not a valid condition export.'),
      );
      return;
    }

    const address = { spreadIndex, conditionId: condition.id };
    const runImport = () => dispatch(importConditionOperation(address, result));

    const importImpact = readStore((state) => getConditionImportImpact(state, spreadIndex, condition.id));
    const unlinkedCount =
      (importImpact?.linkedElementsCount ?? 0) +
      (importImpact?.subConditionsCount ?? 0) +
      (importImpact?.customTextCount ?? 0);

    if (unlinkedCount > 0) {
      dispatch(
        openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
          popupTitle: t('editor-condition-import-confirm-title', 'Replace condition?'),
          textLines: [
            t('editor-condition-import-confirm', 'This replaces {{options}} options and unlinks {{count}} layer(s).', {
              options: condition.options.length,
              count: unlinkedCount,
            }),
          ],
          options: [
            { title: t('editor-cancel') },
            { title: t('editor-condition-import-confirm-action', 'Replace'), onConfirm: runImport },
          ],
          hideTitleIcon: true,
        }),
      );
    } else {
      runImport();
    }
    logEvent('condtion imported', undefined);
  }

  // Only the owning spread's card sits in the reorderable list, so only it can
  // be dragged. A foreign card still gets the menu — an author who has
  // navigated to page 2 must be able to rename an option or add a text variant
  // without first working out which page stores the picklist.
  const draggable = isBuilding && !optionsOnly;

  // See `optionsOnly`: a borrowed card keeps the nested picklists and drops the
  // elements. Without this the buyer could pick an option here and never be
  // offered the follow-up choice it reveals.
  const visibleChildren = optionsOnly ? children.filter((child) => child.type === 'condition') : children;

  useFocusElementOnFlag(conditionEditorOverlay.visible, divRef);

  useEffect(() => {
    onConditionCreated?.(condition.id, onEdit);
    if (consumePendingOpen(condition.id)) {
      onEdit();
    }
  }, []);

  return (
    <div
      ref={divRef}
      className={cn(styles.PersonalizationCondition, 'cy-personalization-condition', {
        [styles.isEditing]: conditionEditorOverlay.visible,
        [styles.static]: !isBuilding,
      })}
    >
      <div
        className={cn({ [styles.content]: draggable, [styles.menuHost]: isBuilding && !draggable }, styles.condition)}
        onMouseDown={draggable ? dragHandle.onMouseDown : undefined}
      >
        <div className={cn(styles.name, 'cy-personalization-condition-name')}>
          {condition.name}
          {isBuilding && (
            <>
              <SettingsMenu className={cn(styles.menuBtn, 'cy-more-button-personalization')}>
                <SettingsMenuItem className={cn(styles.button, 'cy-edit')} onClick={onEdit}>
                  <IconEdit className={styles.editIcon} />
                  {t('editor-edit')}
                </SettingsMenuItem>

                {/* Both reach into the elements linked to this condition, which
                    a foreign card is not showing — offer them where the author
                    can see what they affect. */}
                {!optionsOnly && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-remove')} onClick={onDelete}>
                    <IconBin />
                    {t('editor-remove')}
                  </SettingsMenuItem>
                )}

                {!optionsOnly && condition.options.length > 1 && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-apply-style-to-all')} onClick={onApplyStyleToAll}>
                    <IconApplyToAllAreas />
                    {t('editor-apply-text-style-to-all', 'Apply text style to all options')}
                  </SettingsMenuItem>
                )}

                {/* Duplicate/Export/Import all act on the condition's own
                    definition, not on elements a foreign card is not showing —
                    offered next to Remove/Apply style for the same reason. */}
                {!optionsOnly && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-duplicate')} onClick={onDuplicate}>
                    <span className={styles.menuIcon}>
                      <IconCopy />
                    </span>
                    {t('editor-condition-duplicate', 'Duplicate')}
                  </SettingsMenuItem>
                )}
                {!optionsOnly && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-export')} onClick={onExport}>
                    <span className={styles.menuIcon}>
                      <IconDownload />
                    </span>
                    {t('editor-condition-export', 'Export')}
                  </SettingsMenuItem>
                )}
                {!optionsOnly && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-import')} onClick={onImportClick}>
                    <span className={styles.menuIcon}>
                      <IconImport />
                    </span>
                    {t('editor-condition-import', 'Import')}
                  </SettingsMenuItem>
                )}
              </SettingsMenu>
              <input
                ref={importInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                className="cy-condition-import-input"
                onChange={onImportFileSelected}
              />
              {conditionEditorOverlay.render(
                t('editor-condition-settings'),
                <ConditionSettings condition={condition} conditionGroup={conditionGroup} spreadIndex={spreadIndex} />,
              )}
            </>
          )}
        </div>
        {condition.description && (
          <div className={cn(styles.description, 'cy-personalization-condition-description')}>
            {condition.description}
          </div>
        )}
        <ConditionOptions condition={condition} onSelectOption={onSelectOption} />
      </div>
      {/* The authoring tree belongs to the owning spread's panel only: a
          borrowed card is a remote control, captioned with where the picklist
          is stored, not a second place to restructure the tree. */}
      {isBuilding && !optionsOnly && (
        <ConditionElements
          spreadIndex={spreadIndex}
          conditionGroup={conditionGroup}
          parentCondition={condition}
          elements={children}
          isEditing={conditionEditorOverlay.visible}
          textOnly={textOnly}
          className={styles.conditionElement}
        />
      )}
      {!isBuilding && (!optionsOnly || visibleChildren.length > 0) && (
        <PersonalizationElements
          childrenKey={getConditionChildrenKey(condition.id, condition.activeOptionId)}
          spreadIndex={spreadIndex}
          conditionGroup={conditionGroup}
          elements={visibleChildren}
          personalizationElementClassName={styles.conditionElement}
          optionsOnly={optionsOnly}
        />
      )}
    </div>
  );
}

export default React.memo(PersonalizationCondition);
